import type {
  Adjustment,
  BillingAuditEntry,
  BillingInvoice,
  FiscalDocument,
  Folio,
  FolioItem,
  Payment,
  Refund,
} from "../types/billing";
import type {
  CreateAdjustmentRequestDto,
  CreateBillingFolioRequestDto,
  CreateRefundRequestDto,
  GenerateBillingInvoiceRequestDto,
  PostFolioChargeRequestDto,
  SettleFolioBalanceRequestDto,
} from "./contracts/dtos.ts";
import {
  validateCreateAdjustmentRequest,
  validateCreateBillingFolioRequest,
  validateCreateRefundRequest,
  validateGenerateBillingInvoiceRequest,
  validatePostFolioChargeRequest,
  validateSettleFolioBalanceRequest,
} from "./contracts/schemas.ts";
import { createBillingError, toBillingErrorResponse } from "./errors.ts";
import type { FiscalIntegrationProvider } from "./fiscal.ts";
import type { BillingInvoiceProvider } from "./invoice.ts";
import type { BillingContext } from "./types.ts";

type IdFactory = (prefix: string) => string;

function hasErrors(fieldErrors: Record<string, string>) {
  return Object.values(fieldErrors).some(Boolean);
}

function roundCurrency(value: number) {
  return Number(value.toFixed(2));
}

function buildAudit(
  idFactory: IdFactory,
  now: string,
  input: Omit<BillingAuditEntry, "id" | "timestamp">
): BillingAuditEntry {
  return {
    ...input,
    id: idFactory("bill-audit"),
    timestamp: now,
  };
}

function findFolioOrThrow(folioId: string, context: BillingContext) {
  const folio = context.folios.find((entry) => entry.id === folioId);
  if (!folio) throw createBillingError("FOLIO_NOT_FOUND", "Folio not found.");
  return folio;
}

function recalculateFolio(
  folio: Folio,
  items: FolioItem[],
  payments: Payment[],
  refunds: Refund[]
): Folio {
  const folioItems = items.filter((entry) => entry.folioId === folio.id && entry.status === "posted");
  const folioPayments = payments.filter((entry) => entry.folioId === folio.id && entry.status !== "failed");
  const folioRefunds = refunds.filter((entry) => entry.folioId === folio.id && entry.status === "processed");
  const totalCharges = roundCurrency(folioItems.reduce((sum, entry) => sum + entry.amount, 0));
  const totalPayments = roundCurrency(folioPayments.reduce((sum, entry) => sum + entry.allocatedAmount, 0));
  const totalRefunds = roundCurrency(folioRefunds.reduce((sum, entry) => sum + entry.amount, 0));
  const balance = roundCurrency(totalCharges - totalPayments + totalRefunds);

  return {
    ...folio,
    totalCharges,
    totalPayments,
    totalRefunds,
    balance,
    status: balance === 0 ? "closed" : balance > 0 && totalPayments > 0 ? "settlement_pending" : "open",
    closedAt: balance === 0 ? folio.closedAt : undefined,
  };
}

export function createBillingFolio(
  request: CreateBillingFolioRequestDto,
  context: BillingContext,
  dependencies: { idFactory: IdFactory }
) {
  try {
    const fieldErrors = validateCreateBillingFolioRequest(request);
    if (hasErrors(fieldErrors)) throw createBillingError("VALIDATION_ERROR", "Folio request is invalid.", fieldErrors);

    const folio: Folio = {
      id: request.folioId,
      guestId: request.guestId,
      currency: request.currency,
      reservationId: request.reservationId,
      stayId: request.stayId,
      groupReservationId: request.groupReservationId,
      groupRoomId: request.groupRoomId,
      folioType: request.folioType ?? "reservation",
      balance: 0,
      totalCharges: 0,
      totalPayments: 0,
      totalRefunds: 0,
      status: "open",
    };

    return {
      ok: true as const,
      folio,
      auditEntries: [
        buildAudit(dependencies.idFactory, context.now, {
          folioId: folio.id,
          action: "folio_created",
          actor: request.actor,
          message: `Folio ${folio.id} created.`,
        }),
      ],
    };
  } catch (error) {
    return toBillingErrorResponse(error);
  }
}

export function postFolioCharge(
  request: PostFolioChargeRequestDto,
  context: BillingContext,
  dependencies: { idFactory: IdFactory }
) {
  try {
    const fieldErrors = validatePostFolioChargeRequest(request);
    if (hasErrors(fieldErrors)) throw createBillingError("VALIDATION_ERROR", "Charge request is invalid.", fieldErrors);

    const folio = findFolioOrThrow(request.folioId, context);
    const auditEntries: BillingAuditEntry[] = [];

    let nextFolio = folio;
    if (folio.status === "closed") {
      if (!request.allowClosedFolioPosting) {
        throw createBillingError("VALIDATION_ERROR", "Charge posting after checkout requires explicit approval.");
      }
      nextFolio = {
        ...folio,
        reopenedAt: context.now,
        status: "open",
      };
      auditEntries.push(
        buildAudit(dependencies.idFactory, context.now, {
          folioId: folio.id,
          action: "folio_reopened",
          actor: request.actor,
          message: `Folio ${folio.id} reopened for a late charge.`,
        })
      );
    }

    const item: FolioItem = {
      id: dependencies.idFactory("item"),
      folioId: folio.id,
      reservationId: nextFolio.reservationId,
      stayId: nextFolio.stayId,
      source: request.source,
      category: request.category,
      amount: roundCurrency(request.amount),
      currency: request.currency,
      description: request.description.trim(),
      status: "posted",
      postedAt: context.now,
      postedBy: request.actor,
      serviceDate: request.serviceDate,
      metadata: request.metadata,
    };

    nextFolio = recalculateFolio(nextFolio, [...context.items, item], context.payments, context.refunds);
    nextFolio = { ...nextFolio, lastPostedAt: context.now };

    auditEntries.push(
      buildAudit(dependencies.idFactory, context.now, {
        folioId: folio.id,
        action: "folio_charge_posted",
        actor: request.actor,
        message: `${request.category} charge posted to folio ${folio.id}.`,
        metadata: {
          amount: item.amount.toFixed(2),
          source: item.source,
        },
      })
    );

    return {
      ok: true as const,
      folio: nextFolio,
      item,
      auditEntries,
    };
  } catch (error) {
    return toBillingErrorResponse(error);
  }
}

export function settleFolioBalance(
  request: SettleFolioBalanceRequestDto,
  context: BillingContext,
  dependencies: { idFactory: IdFactory }
) {
  try {
    const fieldErrors = validateSettleFolioBalanceRequest(request);
    if (hasErrors(fieldErrors)) throw createBillingError("VALIDATION_ERROR", "Settlement request is invalid.", fieldErrors);

    const folio = findFolioOrThrow(request.folioId, context);
    const requestedAmount = roundCurrency(request.allocations.reduce((sum, entry) => sum + entry.amount, 0));
    if (requestedAmount > folio.balance) {
      throw createBillingError("PAYMENT_OVERALLOCATION", "Payment exceeds the current folio balance.");
    }

    const payments: Payment[] = request.allocations.map((allocation) => ({
      id: dependencies.idFactory("pay"),
      folioId: folio.id,
      amount: allocation.amount,
      currency: request.currency,
      method: allocation.method,
      status: "captured",
      ts: context.now,
      reference: allocation.reference,
      allocatedAmount: allocation.amount,
      refundedAmount: 0,
    }));

    const nextFolio = recalculateFolio(folio, context.items, [...context.payments, ...payments], context.refunds);

    return {
      ok: true as const,
      folio: nextFolio,
      payments,
      auditEntries: payments.map((payment) =>
        buildAudit(dependencies.idFactory, context.now, {
          folioId: folio.id,
          paymentId: payment.id,
          action: "payment_captured",
          actor: request.actor,
          message: `Payment ${payment.method} captured for folio ${folio.id}.`,
          metadata: {
            amount: payment.amount.toFixed(2),
          },
        })
      ),
    };
  } catch (error) {
    return toBillingErrorResponse(error);
  }
}

export function createRefund(
  request: CreateRefundRequestDto,
  context: BillingContext,
  dependencies: { idFactory: IdFactory }
) {
  try {
    const fieldErrors = validateCreateRefundRequest(request);
    if (hasErrors(fieldErrors)) throw createBillingError("VALIDATION_ERROR", "Refund request is invalid.", fieldErrors);

    const folio = findFolioOrThrow(request.folioId, context);
    const payment = context.payments.find((entry) => entry.id === request.paymentId && entry.folioId === request.folioId);
    if (!payment) throw createBillingError("PAYMENT_NOT_FOUND", "Payment not found.");

    const remainingRefundable = roundCurrency(payment.allocatedAmount - payment.refundedAmount);
    if (request.amount > remainingRefundable) {
      throw createBillingError("REFUND_EXCEEDS_PAYMENT", "Refund exceeds the original payment amount.");
    }

    const refund: Refund = {
      id: dependencies.idFactory("refund"),
      folioId: folio.id,
      paymentId: payment.id,
      amount: request.amount,
      currency: request.currency,
      reason: request.reason.trim(),
      status: "processed",
      createdAt: context.now,
      processedAt: context.now,
      actor: request.actor,
    };

    const nextPayment: Payment = {
      ...payment,
      refundedAmount: roundCurrency(payment.refundedAmount + refund.amount),
      status:
        payment.refundedAmount + refund.amount >= payment.allocatedAmount
          ? "refunded"
          : "partially_refunded",
    };

    const nextPayments = context.payments.map((entry) => (entry.id === payment.id ? nextPayment : entry));
    const nextFolio = recalculateFolio(folio, context.items, nextPayments, [...context.refunds, refund]);

    return {
      ok: true as const,
      folio: nextFolio,
      refund,
      payment: nextPayment,
      auditEntries: [
        buildAudit(dependencies.idFactory, context.now, {
          folioId: folio.id,
          paymentId: payment.id,
          refundId: refund.id,
          action: "refund_processed",
          actor: request.actor,
          message: `Refund processed for payment ${payment.id}.`,
        }),
      ],
    };
  } catch (error) {
    return toBillingErrorResponse(error);
  }
}

export function createAdjustment(
  request: CreateAdjustmentRequestDto,
  context: BillingContext,
  dependencies: { idFactory: IdFactory }
) {
  try {
    const fieldErrors = validateCreateAdjustmentRequest(request);
    if (hasErrors(fieldErrors)) throw createBillingError("VALIDATION_ERROR", "Adjustment request is invalid.", fieldErrors);

    const folio = findFolioOrThrow(request.folioId, context);
    const signedAmount = request.type === "debit" ? request.amount : -request.amount;
    const adjustment: Adjustment = {
      id: dependencies.idFactory("adj"),
      folioId: folio.id,
      type: request.type,
      amount: roundCurrency(request.amount),
      currency: request.currency,
      reason: request.reason.trim(),
      createdAt: context.now,
      actor: request.actor,
    };

    const item: FolioItem = {
      id: dependencies.idFactory("item"),
      folioId: folio.id,
      reservationId: folio.reservationId,
      stayId: folio.stayId,
      source: "manual",
      category: "adjustment",
      amount: signedAmount,
      currency: request.currency,
      description: `${request.type === "credit" ? "Credit" : "Debit"} adjustment: ${adjustment.reason}`,
      status: "posted",
      postedAt: context.now,
      postedBy: request.actor,
      metadata: {
        adjustmentId: adjustment.id,
      },
    };

    const nextFolio = recalculateFolio(folio, [...context.items, item], context.payments, context.refunds);

    return {
      ok: true as const,
      folio: nextFolio,
      adjustment,
      item,
      auditEntries: [
        buildAudit(dependencies.idFactory, context.now, {
          folioId: folio.id,
          adjustmentId: adjustment.id,
          action: "adjustment_posted",
          actor: request.actor,
          message: `Adjustment posted to folio ${folio.id}.`,
        }),
      ],
    };
  } catch (error) {
    return toBillingErrorResponse(error);
  }
}

export async function generateBillingInvoice(
  request: GenerateBillingInvoiceRequestDto,
  context: BillingContext,
  dependencies: {
    idFactory: IdFactory;
    invoiceProvider: BillingInvoiceProvider;
    fiscalProvider: FiscalIntegrationProvider;
  }
) {
  try {
    const fieldErrors = validateGenerateBillingInvoiceRequest(request);
    if (hasErrors(fieldErrors)) throw createBillingError("VALIDATION_ERROR", "Invoice request is invalid.", fieldErrors);

    const folio = findFolioOrThrow(request.folioId, context);

    let invoice: BillingInvoice;
    try {
      const generated = await dependencies.invoiceProvider.generateInvoice({
        folioId: folio.id,
        guestId: folio.guestId,
        reservationId: folio.reservationId,
        stayId: folio.stayId,
        totalAmount: folio.totalCharges,
        currency: folio.currency,
      });

      invoice = {
        id: dependencies.idFactory("invoice"),
        folioId: folio.id,
        reservationId: folio.reservationId,
        stayId: folio.stayId,
        guestId: folio.guestId,
        totalAmount: folio.totalCharges,
        currency: folio.currency,
        status: "draft",
        createdAt: context.now,
        provider: generated.provider,
        documentId: generated.documentId,
      } as any;
    } catch (error) {
      throw createBillingError("INVOICE_GENERATION_FAILED", error instanceof Error ? error.message : "Invoice generation failed.");
    }

    let fiscalDocument: FiscalDocument | undefined;
    const auditEntries: BillingAuditEntry[] = [
      buildAudit(dependencies.idFactory, context.now, {
        folioId: folio.id,
        invoiceId: invoice.id,
        action: "invoice_generated",
        actor: request.actor,
        message: `Invoice generated for folio ${folio.id}.`,
      }),
    ];

    if (request.fiscalize) {
      try {
        const fiscalized = await dependencies.fiscalProvider.fiscalizeInvoice({
          invoiceId: invoice.id,
          folioId: folio.id,
          totalAmount: (invoice as any).totalAmount || folio.totalCharges,
          currency: invoice.currency,
        });
        fiscalDocument = {
          id: dependencies.idFactory("fiscal"),
          invoiceId: invoice.id,
          folioId: folio.id,
          status: "fiscalized",
          provider: fiscalized.provider,
          createdAt: context.now,
          fiscalizedAt: context.now,
          reference: fiscalized.reference,
        };
        invoice = {
          ...invoice,
          status: "issued",
          fiscalDocumentId: fiscalDocument.id,
        };
        auditEntries.push(
          buildAudit(dependencies.idFactory, context.now, {
            folioId: folio.id,
            invoiceId: invoice.id,
            action: "fiscalized",
            actor: request.actor,
            message: `Invoice ${invoice.id} fiscalized.`,
          })
        );
      } catch (error) {
        const message = error instanceof Error ? error.message : "Fiscal integration failed.";
        invoice = {
          ...invoice,
          status: "failed_compliance",
          errorMessage: message,
        };
        fiscalDocument = {
          id: dependencies.idFactory("fiscal"),
          invoiceId: invoice.id,
          folioId: folio.id,
          status: "failed",
          provider: "unknown",
          createdAt: context.now,
          errorMessage: message,
        };
        auditEntries.push(
          buildAudit(dependencies.idFactory, context.now, {
            folioId: folio.id,
            invoiceId: invoice.id,
            action: "fiscal_failed",
            actor: request.actor,
            message,
          })
        );
        throw createBillingError("FISCAL_INTEGRATION_FAILED", message);
      }
    } else {
      fiscalDocument = {
        id: dependencies.idFactory("fiscal"),
        invoiceId: invoice.id,
        folioId: folio.id,
        status: "not_required",
        provider: "not-required",
        createdAt: context.now,
      };
    }

    return {
      ok: true as const,
      invoice,
      fiscalDocument,
      auditEntries,
    };
  } catch (error) {
    return toBillingErrorResponse(error);
  }
}
